import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";

const VitalsMonitorForm = ({ formData, setFormData, handleChange }) => {
    const { user } = useSelector((state) => state.auth);
    const userName = user?.name || "";

    const [rows, setRows] = useState(
        formData.vitalsRecords && formData.vitalsRecords.length > 0
            ? formData.vitalsRecords.map(r => ({ ...r, nurse: r.nurse || "" }))
            : [
                {
                    id: Date.now(),
                    dateTime: "",
                    bp: "",
                    pulse: "",
                    resp: "",
                    temp: "",
                    rbs: "",
                    fbs: "",
                    weight: "",
                    remarks: "",
                    nurse: userName,
                },
            ]
    );

    // Sync rows with parent formData
    useEffect(() => {
        setFormData({ ...formData, vitalsRecords: rows });
    }, [rows]);

    // Add new row
    const addRow = () => {
        setRows([
            {
                id: Date.now(),
                dateTime: "",
                bp: "",
                pulse: "",
                resp: "",
                temp: "",
                rbs: "",
                fbs: "",
                weight: "",
                remarks: "",
                nurse: userName,
            },
            ...rows,
        ]);
    };

    // Remove row
    const removeRow = (id) => {
        if (rows.length === 1) return;
        Swal.fire({
            title: "Delete this entry?",
            text: "This record will be removed.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete it",
        }).then((res) => {
            if (res.isConfirmed) {
                setRows(rows.filter((r) => r.id !== id));
            }
        });
    };

    // Handle row change
    const handleRowChange = (id, field, value) => {
        setRows(rows.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
    };

    return (
        <div className="p-4">
            {/* === Header === */}
            <div className="col-12 mb-3">
                <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3 px-3 py-1">
                    Client Information
                </small>
            </div>

            {/* === Basic Info === */}
            <div className="row g-3 mb-4">
                <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                        <input
                            type="text"
                            name="name"
                            value={formData?.name || ""}
                            onChange={handleChange}
                            className="form-control"
                            placeholder="Name"
                            required
                            readOnly={!!formData?.patientId || !!formData?.patient_id}
                        />
                        <label>Name</label>
                    </div>
                </div>

                <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                        <input
                            type="date"
                            name="dob"
                            value={formData?.dob || ""}
                            onChange={handleChange}
                            className="form-control"
                            required
                            readOnly={!!formData?.patientId || !!formData?.patient_id}
                        />
                        <label>DOB</label>
                    </div>
                </div>
            </div>

            {/* === Repeater Section === */}
            <div className="col-12 mb-3 d-flex justify-content-between align-items-center">
                <small className="text-dark bg-primary-subtle rounded-4 fw-bold mb-0 px-3 py-1 flex-grow-1 me-3">
                    Vitals Monitoring Records
                </small>
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={addRow}>
                    <i className="ri-add-line me-1"></i> Add Row
                </button>
            </div>

            {rows.map((row, index) => (
                <div key={row.id} className="border rounded-4 p-3 mb-3 shadow-sm bg-light-subtle">
                    <div className="row g-3 align-items-start">
                        {/* S.No */}
                        <div className="col-md-1 pt-4 d-flex align-items-center justify-content-center fw-bold">
                            {index + 1}
                        </div>

                        {/* Date & Time */}
                        <div className="col-md-3">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="datetime-local"
                                    className="form-control"
                                    value={row.dateTime}
                                    onChange={(e) =>
                                        handleRowChange(row.id, "dateTime", e.target.value)
                                    }
                                />
                                <label>Date & Time</label>
                            </div>
                        </div>

                        {/* BP */}
                        <div className="col-md-2">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="text"
                                    className="form-control"
                                    value={row.bp}
                                    onChange={(e) => handleRowChange(row.id, "bp", e.target.value)}
                                />
                                <label>BP</label>
                            </div>
                        </div>

                        {/* Pulse */}
                        <div className="col-md-1">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="number"
                                    className="form-control"
                                    value={row.pulse}
                                    onChange={(e) =>
                                        handleRowChange(row.id, "pulse", e.target.value)
                                    }
                                />
                                <label>Pulse</label>
                            </div>
                        </div>

                        {/* Resp */}
                        <div className="col-md-1">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="number"
                                    className="form-control"
                                    value={row.resp}
                                    onChange={(e) => handleRowChange(row.id, "resp", e.target.value)}
                                />
                                <label>Resp</label>
                            </div>
                        </div>

                        {/* Temp */}
                        <div className="col-md-1">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="number"
                                    className="form-control"
                                    value={row.temp}
                                    onChange={(e) => handleRowChange(row.id, "temp", e.target.value)}
                                />
                                <label>Temp</label>
                            </div>
                        </div>

                        {/* RBS */}
                        <div className="col-md-1">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="number"
                                    className="form-control"
                                    value={row.rbs}
                                    onChange={(e) => handleRowChange(row.id, "rbs", e.target.value)}
                                />
                                <label>RBS</label>
                            </div>
                        </div>

                        {/* FBS */}
                        <div className="col-md-1">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="number"
                                    className="form-control"
                                    value={row.fbs}
                                    onChange={(e) => handleRowChange(row.id, "fbs", e.target.value)}
                                />
                                <label>FBS</label>
                            </div>
                        </div>

                        {/* Weight */}
                        <div className="col-md-1">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="number"
                                    className="form-control"
                                    value={row.weight}
                                    onChange={(e) =>
                                        handleRowChange(row.id, "weight", e.target.value)
                                    }
                                />
                                <label>Weight</label>
                            </div>
                        </div>
                    </div>

                    {/* --- Second Row: Remarks, Nurse, Delete --- */}
                    <div className="row g-3 mt-1 align-items-center">
                        {/* Remarks */}
                        <div className="col-md-8">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="text"
                                    className="form-control"
                                    value={row.remarks}
                                    onChange={(e) =>
                                        handleRowChange(row.id, "remarks", e.target.value)
                                    }
                                />
                                <label>Remarks</label>
                            </div>
                        </div>

                        {/* Nurse */}
                        <div className="col-md-3">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="text"
                                    className="form-control"
                                    value={row.nurse}
                                    onChange={(e) =>
                                        handleRowChange(row.id, "nurse", e.target.value)
                                    }
                                    readOnly
                                />
                                <label>Nurse</label>
                            </div>
                        </div>

                        {/* Delete Button */}
                        <div className="col-md-1 text-center">
                            <button
                                type="button"
                                className="btn btn-icon btn-outline-danger"
                                onClick={() => removeRow(row.id)}
                            >
                                <i className="ri-delete-bin-line"></i>
                            </button>
                        </div>
                    </div>
                </div>
            ))}

            {/* === Footer Section === */}
            <div className="row g-3 mt-4">
                <div className="col-md-4">
                    <div className="form-floating form-floating-outline">
                        <input
                            type="text"
                            name="documentName"
                            value={formData?.documentName || ""}
                            onChange={handleChange}
                            className="form-control"
                            readOnly
                        />
                        <label>Document Name</label>
                    </div>
                </div>

                <div className="col-md-4"></div>
                <div className="col-md-4">
                    <div className="form-floating form-floating-outline">
                        <input
                            type="date"
                            name="effectiveDate"
                            value={formData?.effectiveDate || ""}
                            onChange={handleChange}
                            className="form-control"
                        />
                        <label>Effective Date</label>
                    </div>
                </div>
            </div>

            <div className="row g-3 mt-3">
                <div className="col-md-3">
                    <div className="form-floating form-floating-outline">
                        <input
                            type="text"
                            name="approvedBy"
                            value={formData?.approvedBy || ""}
                            onChange={handleChange}
                            className="form-control"
                        />
                        <label>Approved By</label>
                    </div>
                </div>

                <div className="col-md-3">
                    <div className="form-floating form-floating-outline">
                        <input
                            type="date"
                            name="approvedDate"
                            value={formData?.approvedDate || ""}
                            onChange={handleChange}
                            className="form-control"
                        />
                        <label>Date</label>
                    </div>
                </div>

                <div className="col-md-3">
                    <div className="form-floating form-floating-outline">
                        <input
                            type="text"
                            name="nextRevisionBy"
                            value={formData?.nextRevisionBy || ""}
                            onChange={handleChange}
                            className="form-control"
                        />
                        <label>Next Revision By</label>
                    </div>
                </div>

                <div className="col-md-3">
                    <div className="form-floating form-floating-outline">
                        <input
                            type="date"
                            name="nextRevisionDate"
                            value={formData?.nextRevisionDate || ""}
                            onChange={handleChange}
                            className="form-control"
                        />
                        <label>Date</label>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VitalsMonitorForm;
